import React, { Suspense, lazy, useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { API_CONFIG } from './utils/constants';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import RoleBasedRoute from './components/RoleBasedRoute';
import Sidebar from './components/Sidebar';
import SessionManager from './components/SessionManager';
import RoleRedirect from './components/RoleRedirect';
import ImpersonationBanner from './components/ImpersonationBanner';
import './App.css';

// Auth Pages (lazy loaded)
const Login = lazy(() => import('./pages/auth/Login'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const OTPVerification = lazy(() => import('./pages/auth/OTPVerification'));
const Onboarding = lazy(() => import('./pages/auth/Onboarding'));
const PolicyPage = lazy(() => import('./pages/public/PolicyPage'));
const DetailPage = lazy(() => import('./pages/public/DetailPage'));
const DoctorDetail = lazy(() => import('./pages/public/DoctorDetail'));

// Admin Pages (lazy loaded)
const AdminHome = lazy(() => import('./pages/admin/Home'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminRoles = lazy(() => import('./pages/admin/Roles'));
const AdminPermissions = lazy(() => import('./pages/admin/Permissions'));
const AdminSpecializations = lazy(() => import('./pages/admin/Specializations'));
const AdminDoctorHolidays = lazy(() => import('./pages/admin/DoctorHolidays'));
const AdminDoctors = lazy(() => import('./pages/admin/Doctors'));
const AdminStaff = lazy(() => import('./pages/admin/Staff'));
const AdminPatients = lazy(() => import('./pages/admin/Patients'));
const AdminServices = lazy(() => import('./pages/admin/Services'));
const AdminTests = lazy(() => import('./pages/admin/Tests'));
const AdminAppointments = lazy(() => import('./pages/admin/Appointments'));
const AdminBilling = lazy(() => import('./pages/admin/Billing'));
const AdminPayments = lazy(() => import('./pages/admin/Payments'));
const AdminReports = lazy(() => import('./pages/admin/Reports'));
const AdminCollectionReport = lazy(() => import('./pages/admin/CollectionReport'));
const AdminBusinessSettings = lazy(() => import('./pages/admin/BusinessSettings'));
const AdminAvailabilityManagement = lazy(() => import('./pages/admin/AvailabilityManagement'));
const AdminBookAppointment = lazy(() => import('./pages/admin/BookAppointment'));
const AdminSubscription = lazy(() => import('./pages/admin/Subscription'));
const AdminPatientSearch = lazy(() => import('./pages/admin/PatientSearch'));
const AdminCheckedPatients = lazy(() => import('./pages/admin/CheckedPatients'));
const AdminTreatments = lazy(() => import('./pages/admin/Treatments'));
const AdminDiagnostics = lazy(() => import('./pages/admin/Diagnostics'));
const AdminGallery = lazy(() => import('./pages/admin/Gallery'));
const AdminTestimonials = lazy(() => import('./pages/admin/Testimonials'));

// Doctor Pages (lazy loaded)
const DoctorHome = lazy(() => import('./pages/doctor/Home'));
const DoctorDashboard = lazy(() => import('./pages/doctor/Dashboard'));
const DoctorPatients = lazy(() => import('./pages/doctor/Patients'));
const DoctorAppointments = lazy(() => import('./pages/doctor/Appointments'));
const DoctorAvailabilityManagement = lazy(() => import('./pages/doctor/AvailabilityManagement'));
const DoctorDoctorHolidays = lazy(() => import('./pages/doctor/DoctorHolidays'));
const DoctorBilling = lazy(() => import('./pages/doctor/Billing'));
const DoctorPayments = lazy(() => import('./pages/doctor/Payments'));
const DoctorReports = lazy(() => import('./pages/doctor/Reports'));
const DoctorCollectionReport = lazy(() => import('./pages/doctor/CollectionReport'));
const DoctorSettings = lazy(() => import('./pages/doctor/Settings'));
const DoctorPatientSearch = lazy(() => import('./pages/doctor/PatientSearch'));
const DoctorBookAppointment = lazy(() => import('./pages/doctor/BookAppointment'));
const DoctorMedicines = lazy(() => import('./pages/doctor/Medicines'));
const DoctorTests = lazy(() => import('./pages/doctor/Tests'));
const DoctorCheckedPatients = lazy(() => import('./pages/doctor/CheckedPatients'));

// Staff Pages (lazy loaded)
const StaffDashboard = lazy(() => import('./pages/staff/Dashboard'));
const StaffNoPermission = lazy(() => import('./pages/staff/NoPermission'));
const StaffBookAppointment = lazy(() => import('./pages/staff/BookAppointment'));
const StaffAppointments = lazy(() => import('./pages/staff/Appointments'));
const StaffAvailabilityManagement = lazy(() => import('./pages/staff/AvailabilityManagement'));
const StaffSpecializations = lazy(() => import('./pages/staff/Specializations'));
const StaffDoctorHolidays = lazy(() => import('./pages/staff/DoctorHolidays'));
const StaffDoctors = lazy(() => import('./pages/staff/Doctors'));
const StaffPatients = lazy(() => import('./pages/staff/Patients'));
const StaffServices = lazy(() => import('./pages/staff/Services'));
const StaffTests = lazy(() => import('./pages/staff/Tests'));
const StaffBilling = lazy(() => import('./pages/staff/Billing'));
const StaffPayments = lazy(() => import('./pages/staff/Payments'));
const StaffReports = lazy(() => import('./pages/staff/Reports'));
const StaffCollectionReport = lazy(() => import('./pages/staff/CollectionReport'));
const StaffSettings = lazy(() => import('./pages/staff/Settings'));
const StaffUsers = lazy(() => import('./pages/staff/Users'));
const StaffRoles = lazy(() => import('./pages/staff/Roles'));
const StaffPermissions = lazy(() => import('./pages/staff/Permissions'));
const StaffStaff = lazy(() => import('./pages/staff/Staff'));
const StaffPatientUsers = lazy(() => import('./pages/staff/PatientUsers'));
const StaffBusinessSettings = lazy(() => import('./pages/staff/BusinessSettings'));
const StaffPatientSearch = lazy(() => import('./pages/staff/PatientSearch'));
const StaffCheckedPatients = lazy(() => import('./pages/staff/CheckedPatients'));

// SuperAdmin Pages (lazy loaded)
const SuperAdminDashboard = lazy(() => import('./pages/superadmin/Dashboard'));
const SuperAdminUserApprovals = lazy(() => import('./pages/superadmin/UserApprovals'));
const SuperAdminSubscriptionPlans = lazy(() => import('./pages/superadmin/SubscriptionPlans'));
const SuperAdminSubscriptions = lazy(() => import('./pages/superadmin/Subscriptions'));
const SuperAdminCoupons = lazy(() => import('./pages/superadmin/Coupons'));
const SuperAdminSettings = lazy(() => import('./pages/superadmin/Settings'));
const SuperAdminUserDirectory = lazy(() => import('./pages/superadmin/UserDirectory'));

// Patient Pages (lazy loaded)
const PatientHome = lazy(() => import('./pages/patient/Home'));
const PatientDashboard = lazy(() => import('./pages/patient/Dashboard'));
const PatientAppointments = lazy(() => import('./pages/patient/Appointments'));
const PatientBookAppointment = lazy(() => import('./pages/patient/BookAppointment'));
const PatientDoctors = lazy(() => import('./pages/patient/Doctors'));
const PatientProfile = lazy(() => import('./pages/patient/Profile'));
const PatientPrescriptions = lazy(() => import('./pages/patient/Prescriptions'));
const PatientBills = lazy(() => import('./pages/patient/Bills'));
const PatientSettings = lazy(() => import('./pages/patient/Settings'));
const PatientMedicalRecords = lazy(() => import('./pages/patient/MedicalRecords'));
const PatientFamilyMembers = lazy(() => import('./pages/patient/FamilyMembers'));
const PatientEmergencyContacts = lazy(() => import('./pages/patient/EmergencyContacts'));

// Syncs logged-in user's hospital branding (name, logo, color) into ThemeContext
function HospitalNameSync() {
  const { user } = useAuth();
  const { setHospitalName, setLogoUrl, setPrimaryColor, setFaviconUrl, setLogoAnimation } = useTheme();

  useEffect(() => {
    setHospitalName(user?.hospital_name || '');
  }, [user?.hospital_name, setHospitalName]);

  // Re-fetch branding when user's hospital changes (e.g. after impersonation)
  // Reset to defaults when no hospital (e.g. returning to Super Admin)
  useEffect(() => {
    if (!user?.hospital_code) {
      // Reset to platform defaults
      setPrimaryColor('#4cb7ec');
      setLogoUrl('');
      setLogoAnimation('none');
      return;
    }
    const fetchBranding = async () => {
      try {
        const url = `${API_CONFIG.BASE_URL}/api/branding?hospital_code=${user.hospital_code}`;
        const response = await fetch(url);
        const data = await response.json();
        if (!data.status || !data.data) return;
        const d = data.data;
        if (d.primary_color) setPrimaryColor(d.primary_color);
        if (d.logo_url) setLogoUrl(d.logo_url);
        if (d.favicon_url) setFaviconUrl(d.favicon_url);
        if (d.logo_animation) setLogoAnimation(d.logo_animation);
      } catch (_) {
        /* silently fail */
      }
    };
    fetchBranding();
  }, [user?.hospital_code, setPrimaryColor, setLogoUrl, setFaviconUrl, setLogoAnimation]);

  return null;
}

function MobileBrandingBadge() {
  const [visible, setVisible] = useState(true);
  const badgeRef = useRef(null);
  const timerRef = useRef(null);

  const checkOverlap = useCallback(() => {
    const badge = badgeRef.current;
    if (!badge || window.innerWidth >= 992) return;

    const rect = badge.getBoundingClientRect();
    // Temporarily hide to check what's behind
    badge.style.visibility = 'hidden';
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const elements = document.elementsFromPoint(centerX, centerY);
    badge.style.visibility = '';

    // Filter out generic containers - if only body/html/app wrapper, area is "empty"
    const hasContent = elements.some((el) => {
      if (el === badge || badge.contains(el)) return false;
      const tag = el.tagName.toLowerCase();
      if (tag === 'html' || tag === 'body') return false;
      // Skip the app wrapper div and main layout containers
      if (el.classList.contains('App') || el.classList.contains('main-content')) return false;
      // Check if element has visible content (text, images, inputs, etc.)
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
      return true;
    });

    if (hasContent) {
      // Content underneath - show briefly then hide after 3s
      setVisible(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setVisible(false), 3000);
    } else {
      // Empty space - keep visible
      clearTimeout(timerRef.current);
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    // Initial check after mount
    const initialTimer = setTimeout(checkOverlap, 500);

    // Re-check on scroll, resize, and route changes
    const handleEvent = () => {
      setVisible(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(checkOverlap, 300);
    };

    window.addEventListener('scroll', handleEvent, true);
    window.addEventListener('resize', handleEvent);

    // Observe DOM changes (route changes cause content updates)
    const observer = new MutationObserver(() => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(checkOverlap, 500);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(timerRef.current);
      window.removeEventListener('scroll', handleEvent, true);
      window.removeEventListener('resize', handleEvent);
      observer.disconnect();
    };
  }, [checkOverlap]);

  return (
    <a
      ref={badgeRef}
      href="https://webdekho.in"
      target="_blank"
      rel="noopener noreferrer"
      className={`managed-by-badge${visible ? '' : ' managed-by-badge-hidden'}`}
    >
      <img src="https://webdekho.in/images/wpl_qms.png" alt="Webdekho" className="managed-by-logo" />
    </a>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <Router basename={process.env.PUBLIC_URL || "/"}>
              <SubscriptionProvider>
                <RealtimeProvider>
                  <div className="App">
                    <HospitalNameSync />
                    <ImpersonationBanner />
                    <SessionManager />
                    <Suspense fallback={<LoadingSpinner />}>
                      <Routes>
                        {/* Public Routes */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/verify-otp" element={<OTPVerification />} />
                        <Route path="/onboarding" element={<Onboarding />} />
                        <Route path="/page/:slug" element={<PolicyPage />} />
                        <Route path="/page/:type/:slug" element={<DetailPage />} />
                        <Route path="/doctor/:id" element={<DoctorDetail />} />

                        {/* Root redirect based on user role */}
                        <Route path="/" element={<RoleRedirect />} />

                        {/* SuperAdmin Routes */}
                        <Route
                          path="/superadmin/dashboard"
                          element={
                            <RoleBasedRoute allowedRoles={['superadmin']}>
                              <Sidebar>
                                <SuperAdminDashboard />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/superadmin/user-approvals"
                          element={
                            <RoleBasedRoute allowedRoles={['superadmin']}>
                              <Sidebar>
                                <SuperAdminUserApprovals />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/superadmin/subscription-plans"
                          element={
                            <RoleBasedRoute allowedRoles={['superadmin']}>
                              <Sidebar>
                                <SuperAdminSubscriptionPlans />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/superadmin/subscriptions"
                          element={
                            <RoleBasedRoute allowedRoles={['superadmin']}>
                              <Sidebar>
                                <SuperAdminSubscriptions />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/superadmin/coupons"
                          element={
                            <RoleBasedRoute allowedRoles={['superadmin']}>
                              <Sidebar>
                                <SuperAdminCoupons />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/superadmin/settings"
                          element={
                            <RoleBasedRoute allowedRoles={['superadmin']}>
                              <Sidebar>
                                <SuperAdminSettings />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/superadmin/user-directory"
                          element={
                            <RoleBasedRoute allowedRoles={['superadmin']}>
                              <Sidebar>
                                <SuperAdminUserDirectory />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />

                        {/* Admin Routes */}
                        <Route
                          path="/admin/home"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminHome />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/dashboard"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminDashboard />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/users"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminUsers />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/roles"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminRoles />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/permissions"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminPermissions />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/specializations"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminSpecializations />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/doctor-holidays"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminDoctorHolidays />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/doctors"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminDoctors />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/staff"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminStaff />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/patients"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminPatients />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route path="/admin/patient-users" element={<Navigate to="/admin/patients" replace />} />
                        <Route
                          path="/admin/services"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminServices />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/tests"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminTests />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/book-appointment"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminBookAppointment />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/appointments"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminAppointments />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/availability-management"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminAvailabilityManagement />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/billing"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminBilling />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/payments"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminPayments />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/reports"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminReports />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/collection-report"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminCollectionReport />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/checked-patients"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminCheckedPatients />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/subscription"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminSubscription />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route path="/admin/settings" element={<Navigate to="/admin/business-settings" replace />} />
                        <Route
                          path="/admin/business-settings"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminBusinessSettings />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/patient-search"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminPatientSearch />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/treatments"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminTreatments />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/diagnostics"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminDiagnostics />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/gallery"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminGallery />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/admin/testimonials"
                          element={
                            <RoleBasedRoute allowedRoles={['admin']}>
                              <Sidebar>
                                <AdminTestimonials />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />

                        {/* Doctor Routes - scoped to doctor-appropriate features only */}
                        <Route
                          path="/doctor/home"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorHome />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/dashboard"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorDashboard />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/appointments"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorAppointments />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/patients"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorPatients />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/availability-management"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorAvailabilityManagement />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/doctor-holidays"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorDoctorHolidays />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/billing"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorBilling />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/payments"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorPayments />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/reports"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorReports />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/collection-report"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorCollectionReport />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/settings"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorSettings />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/patient-search"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorPatientSearch />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/book-appointment"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorBookAppointment />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/medicines"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorMedicines />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/doctor/checked-patients"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorCheckedPatients />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        {/* Redirect removed doctor pages to home */}
                        <Route path="/doctor/users" element={<Navigate to="/doctor/home" replace />} />
                        <Route path="/doctor/roles" element={<Navigate to="/doctor/home" replace />} />
                        <Route path="/doctor/permissions" element={<Navigate to="/doctor/home" replace />} />
                        <Route path="/doctor/staff" element={<Navigate to="/doctor/home" replace />} />
                        <Route path="/doctor/doctors" element={<Navigate to="/doctor/home" replace />} />
                        <Route path="/doctor/specializations" element={<Navigate to="/doctor/home" replace />} />
                        <Route path="/doctor/services" element={<Navigate to="/doctor/home" replace />} />
                        <Route
                          path="/doctor/tests"
                          element={
                            <RoleBasedRoute allowedRoles={['doctor']}>
                              <Sidebar>
                                <DoctorTests />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route path="/doctor/patient-users" element={<Navigate to="/doctor/patients" replace />} />

                        {/* Staff Routes - each route has requiredModule for access control */}
                        <Route
                          path="/staff/home"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']}>
                              <Sidebar>
                                <StaffNoPermission />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/dashboard"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="dashboard">
                              <Sidebar>
                                <StaffDashboard />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        {/* User Management pages - gated by module access */}
                        <Route
                          path="/staff/users"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="users">
                              <Sidebar>
                                <StaffUsers />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/roles"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="roles">
                              <Sidebar>
                                <StaffRoles />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/permissions"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="permissions">
                              <Sidebar>
                                <StaffPermissions />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/specializations"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="specializations">
                              <Sidebar>
                                <StaffSpecializations />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/doctor-holidays"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="doctor-holidays">
                              <Sidebar>
                                <StaffDoctorHolidays />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/doctors"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="doctors">
                              <Sidebar>
                                <StaffDoctors />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/staff"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="staff">
                              <Sidebar>
                                <StaffStaff />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/patients"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="patients">
                              <Sidebar>
                                <StaffPatients />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/patient-users"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="patient-users">
                              <Sidebar>
                                <StaffPatientUsers />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/services"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="services">
                              <Sidebar>
                                <StaffServices />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/tests"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="tests">
                              <Sidebar>
                                <StaffTests />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/book-appointment"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="appointments">
                              <Sidebar>
                                <StaffBookAppointment />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/appointments"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="appointments">
                              <Sidebar>
                                <StaffAppointments />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/availability-management"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="availability">
                              <Sidebar>
                                <StaffAvailabilityManagement />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/billing"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="billing">
                              <Sidebar>
                                <StaffBilling />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/payments"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="payments">
                              <Sidebar>
                                <StaffPayments />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/reports"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="reports">
                              <Sidebar>
                                <StaffReports />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/collection-report"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="reports">
                              <Sidebar>
                                <StaffCollectionReport />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/checked-patients"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="reports">
                              <Sidebar>
                                <StaffCheckedPatients />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/business-settings"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="business-settings">
                              <Sidebar>
                                <StaffBusinessSettings />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/staff/settings"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="settings">
                              <Sidebar>
                                <StaffSettings />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />

                        <Route
                          path="/staff/patient-search"
                          element={
                            <RoleBasedRoute allowedRoles={['staff']} requiredModule="patient-search">
                              <Sidebar>
                                <StaffPatientSearch />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />

                        {/* Patient Routes */}
                        <Route
                          path="/patient/home"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientHome />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/dashboard"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientDashboard />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/book-appointment"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientBookAppointment />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/appointments"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientAppointments />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/doctors"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientDoctors />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/prescriptions"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientPrescriptions />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/bills"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientBills />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/profile"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientProfile />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/settings"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientSettings />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/medical-records"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientMedicalRecords />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/family-members"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientFamilyMembers />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />
                        <Route
                          path="/patient/emergency-contacts"
                          element={
                            <RoleBasedRoute allowedRoles={['patient']}>
                              <Sidebar>
                                <PatientEmergencyContacts />
                              </Sidebar>
                            </RoleBasedRoute>
                          }
                        />

                        {/* Catch all route */}
                        <Route path="*" element={<RoleRedirect />} />
                      </Routes>
                    </Suspense>
                    {/* Managed by badge - mobile only with auto-hide on content overlap */}
                    <MobileBrandingBadge />
                  </div>
                </RealtimeProvider>
              </SubscriptionProvider>
            </Router>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
